import React, { useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Tabs, Tab,
  IconButton, Button, Chip, List, ListItem, ListItemText,
  ListItemSecondaryAction, Menu, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, FormControl,
  InputLabel, SpeedDial, SpeedDialAction, SpeedDialIcon, Grid
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TaskIcon from '@mui/icons-material/Assignment';
import PlantIcon from '@mui/icons-material/LocalFlorist';
import GardenIcon from '@mui/icons-material/Yard';
import AreaIcon from '@mui/icons-material/GridOn';
import SpaceIcon from '@mui/icons-material/Landscape';
import PeopleIcon from '@mui/icons-material/People';
import FarmAvatar from '../components/FarmAvatar';
import toast from 'react-hot-toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const FarmManagement: React.FC = () => {
  const navigate = useNavigate();
  const { farmId } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  
  // Mock data - replace with API calls
  const [farmData, setFarmData] = useState({
    name: 'Urban Oasis',
    avatar: { type: 'shape' as const, data: 'hexagon:gradient:#22c55e:#06b6d4:45' },
    description: 'Sustainable urban farming in the heart of the city',
    stats: {
      spaces: 3,
      areas: 5,
      gardens: 12,
      plants: 47,
      tasks: 8,
      members: 4
    }
  });

  const [spaces, setSpaces] = useState([
    { id: 1, name: 'Backyard', type: 'backyard', gardenCount: 4, plantCount: 18, taskCount: 3 },
    { id: 2, name: 'Front Yard', type: 'frontyard', gardenCount: 3, plantCount: 12, taskCount: 2 },
    { id: 3, name: 'Side Garden', type: 'side_yard', gardenCount: 2, plantCount: 8, taskCount: 1 },
  ]);

  const [areas, setAreas] = useState([
    { id: 1, name: 'Herb Corner', type: 'garden_bed', space: 'Backyard', plantCount: 8, taskCount: 1 },
    { id: 2, name: 'Vegetable Patch', type: 'raised_bed', space: 'Backyard', plantCount: 15, taskCount: 2 },
  ]);

  const [gardens, setGardens] = useState([
    { id: 1, name: 'Tomato Garden', type: 'outdoor', area: 'Vegetable Patch', plantCount: 6, taskCount: 1 },
    { id: 2, name: 'Basil Collection', type: 'outdoor', area: 'Herb Corner', plantCount: 4, taskCount: 0 },
  ]);

  const [plants, setPlants] = useState([
    { id: 1, name: 'Cherry Tomato #1', garden: 'Tomato Garden', variety: 'Cherry', health: 'healthy', taskCount: 1 },
    { id: 2, name: 'Beefsteak Tomato', garden: 'Tomato Garden', variety: 'Beefsteak', health: 'needs_water', taskCount: 0 },
  ]);

  const [tasks, setTasks] = useState([
    { id: 1, title: 'Water tomatoes', entity: 'garden', entityName: 'Tomato Garden', dueDate: 'Today', priority: 'high' },
    { id: 2, title: 'Check for pests', entity: 'space', entityName: 'Backyard', dueDate: 'Tomorrow', priority: 'medium' },
    { id: 3, title: 'Harvest basil', entity: 'plant', entityName: 'Genovese Basil', dueDate: 'This week', priority: 'low' },
  ]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleCreateTask = async () => {
    try {
      // Mock XP reward
      toast.success('Task created! +10 XP');
      setOpenTaskDialog(false);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const speedDialActions = [
    { icon: <PlantIcon />, name: 'Add Plant', onClick: () => navigate(`/farms/${farmId}/plants/add`) },
    { icon: <GardenIcon />, name: 'Create Garden', onClick: () => navigate(`/farms/${farmId}/gardens/create`) },
    { icon: <AreaIcon />, name: 'Add Area', onClick: () => navigate(`/farms/${farmId}/areas/add`) },
    { icon: <SpaceIcon />, name: 'Create Space', onClick: () => navigate(`/farms/${farmId}/spaces/create`) },
    { icon: <TaskIcon />, name: 'Add Task', onClick: () => setOpenTaskDialog(true) },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 3, pb: 3 }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2}>
            <FarmAvatar avatar={farmData.avatar} size={60} />
            <Box flex={1}>
              <Typography variant="h5" fontWeight="bold">
                {farmData.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {farmData.description}
              </Typography>
            </Box>
            <IconButton sx={{ color: 'white' }}>
              <EditIcon />
            </IconButton>
          </Box>

          {/* Stats */}
          <Box display="flex" gap={2} mt={2} flexWrap="wrap">
            <Chip label={`${farmData.stats.spaces} Spaces`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            <Chip label={`${farmData.stats.areas} Areas`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            <Chip label={`${farmData.stats.gardens} Gardens`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            <Chip label={`${farmData.stats.plants} Plants`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            <Chip 
              label={`${farmData.stats.tasks} Active Tasks`} 
              sx={{ bgcolor: farmData.stats.tasks > 0 ? 'warning.main' : 'rgba(255,255,255,0.2)', color: 'white' }} 
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        {/* Quick Actions */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate(`/farms/${farmId}/members`)}
              >
                Manage Members ({farmData.stats.members})
              </Button>
              <Button
                variant="outlined"
                startIcon={<TaskIcon />}
                color="warning"
                onClick={() => setTabValue(5)}
              >
                View All Tasks ({farmData.stats.tasks})
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label={`Spaces (${spaces.length})`} />
          <Tab label={`Areas (${areas.length})`} />
          <Tab label={`Gardens (${gardens.length})`} />
          <Tab label={`Plants (${plants.length})`} />
          <Tab label={`Tasks (${tasks.length})`} />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Activity
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Named your farm"
                        secondary="Pioneer Badge earned! +50 XP"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Created first space"
                        secondary="Getting Started Badge earned! +25 XP"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Added 5 plants"
                        secondary="+50 XP"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Achievements
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Chip icon={<Typography>🏆</Typography>} label="Pioneer" color="primary" />
                    <Chip icon={<Typography>🌱</Typography>} label="Getting Started" color="success" />
                    <Chip icon={<Typography>👨‍🌾</Typography>} label="Lead Farmer" color="secondary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Spaces Tab */}
        <TabPanel value={tabValue} index={1}>
          <List>
            {spaces.map(space => (
              <ListItem key={space.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <SpaceIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{space.name}</Typography>
                      <Chip label={space.type} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box display="flex" gap={2} mt={1}>
                      <Typography variant="body2">{space.gardenCount} gardens</Typography>
                      <Typography variant="body2">{space.plantCount} plants</Typography>
                      {space.taskCount > 0 && (
                        <Chip label={`${space.taskCount} tasks`} size="small" color="warning" />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={(e) => handleMenuClick(e, space)}>
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Areas Tab */}
        <TabPanel value={tabValue} index={2}>
          <List>
            {areas.map(area => (
              <ListItem key={area.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <AreaIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{area.name}</Typography>
                      <Chip label={area.type} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box display="flex" gap={2} mt={1}>
                      <Typography variant="body2">in {area.space}</Typography>
                      <Typography variant="body2">{area.plantCount} plants</Typography>
                      {area.taskCount > 0 && (
                        <Chip label={`${area.taskCount} tasks`} size="small" color="warning" />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={(e) => handleMenuClick(e, area)}>
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Gardens Tab */}
        <TabPanel value={tabValue} index={3}>
          <List>
            {gardens.map(garden => (
              <ListItem key={garden.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <GardenIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{garden.name}</Typography>
                      <Chip label={garden.type} size="small" />
                    </Box>
                  }
                  secondary={
                    <Box display="flex" gap={2} mt={1}>
                      <Typography variant="body2">in {garden.area}</Typography>
                      <Typography variant="body2">{garden.plantCount} plants</Typography>
                      {garden.taskCount > 0 && (
                        <Chip label={`${garden.taskCount} tasks`} size="small" color="warning" />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={(e) => handleMenuClick(e, garden)}>
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Plants Tab */}
        <TabPanel value={tabValue} index={4}>
          <List>
            {plants.map(plant => (
              <ListItem key={plant.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <PlantIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{plant.name}</Typography>
                      <Chip 
                        label={plant.health} 
                        size="small" 
                        color={plant.health === 'healthy' ? 'success' : 'warning'}
                      />
                    </Box>
                  }
                  secondary={
                    <Box display="flex" gap={2} mt={1}>
                      <Typography variant="body2">{plant.variety}</Typography>
                      <Typography variant="body2">in {plant.garden}</Typography>
                      {plant.taskCount > 0 && (
                        <Chip label={`${plant.taskCount} tasks`} size="small" color="warning" />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={(e) => handleMenuClick(e, plant)}>
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={tabValue} index={5}>
          <List>
            {tasks.map(task => (
              <ListItem key={task.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <TaskIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">{task.title}</Typography>
                      <Chip 
                        label={task.priority} 
                        size="small" 
                        color={task.priority === 'high' ? 'error' : task.priority === 'medium' ? 'warning' : 'default'}
                      />
                    </Box>
                  }
                  secondary={
                    <Box display="flex" gap={2} mt={1}>
                      <Typography variant="body2">{task.entity}: {task.entityName}</Typography>
                      <Typography variant="body2">Due: {task.dueDate}</Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Button size="small" variant="contained" color="primary">
                    Complete
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>
      </Container>

      {/* SpeedDial for adding items */}
      <SpeedDial
        ariaLabel="Add items"
        sx={{ position: 'fixed', bottom: 80, right: 16 }}
        icon={<SpeedDialIcon />}
        open={speedDialOpen}
        onOpen={() => setSpeedDialOpen(true)}
        onClose={() => setSpeedDialOpen(false)}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={() => {
              setSpeedDialOpen(false);
              action.onClick();
            }}
          />
        ))}
      </SpeedDial>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          // Navigate to edit
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          setOpenTaskDialog(true);
        }}>
          <TaskIcon fontSize="small" sx={{ mr: 1 }} />
          Add Task
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          setOpenDeleteDialog(true);
        }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Task Dialog */}
      <Dialog open={openTaskDialog} onClose={() => setOpenTaskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Task Title"
            margin="normal"
            placeholder="e.g., Water the tomatoes"
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Priority</InputLabel>
            <Select defaultValue="medium">
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Due Date"
            type="date"
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTaskDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateTask} variant="contained">
            Create Task (+10 XP)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedItem?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={() => {
            // Handle delete
            setOpenDeleteDialog(false);
            toast.success('Item deleted');
          }} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmManagement;